import axios, { type AxiosError, type AxiosInstance } from 'axios'
import { useAuthStore } from '@shared/stores/auth'

export type ErrorInterceptor = (error: AxiosError) => void

export interface ServiceApiConfigParams {
  basePath: string
  accessToken: string
  baseOptions: { headers: { Authorization: string } }
}

/**
 * Shared builder for every @skynamo/*_service_api client: derives the service
 * base URL from the auth store's data-centre and returns the Configuration
 * constructor params plus a bearer-authed axios instance. Each service singleton
 * passes `params` to its own package's Configuration (the Configuration classes
 * are structurally identical but nominally distinct per package, so this base
 * cannot own the typed Configuration itself).
 */
export async function createServiceApiConfiguration(
  subdomain: string,
  onError?: ErrorInterceptor
): Promise<{ params: ServiceApiConfigParams; instance: AxiosInstance }> {
  const authStore = useAuthStore()
  await authStore.update()
  const baseUrl = `https://${subdomain}.svc.${authStore.dc}`
  const authHeader = `Bearer ${authStore.authToken}`
  const params: ServiceApiConfigParams = {
    basePath: baseUrl,
    accessToken: authStore.authToken,
    baseOptions: { headers: { Authorization: authHeader } },
  }
  const instance = axios.create({ baseURL: baseUrl, headers: { Authorization: authHeader } })
  if (onError) {
    instance.interceptors.response.use(undefined, (error: AxiosError) => onError(error))
  }
  return { params, instance }
}
